import { useState, useEffect } from 'react';
import { 
  Plus, Edit2, Trash2, Loader2, X, Ticket, Calendar, 
  Percent, DollarSign, CheckCircle, XCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api/admin/shop';

export default function AdminShopCouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: '',
    min_cart_value: 0,
    max_uses: '',
    expires_at: '',
    is_active: true
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCoupons();
  }, []);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('dharmbook_admin_token')}` }
  });

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/coupons`, getAuthHeaders());
      setCoupons(response.data.coupons);
    } catch (error) {
      toast.error('Failed to fetch coupons');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingCoupon(null);
    setFormData({
      code: '',
      discount_type: 'percentage',
      discount_value: '',
      min_cart_value: 0,
      max_uses: '',
      expires_at: '',
      is_active: true
    });
    setShowModal(true);
  };

  const openEditModal = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code || '',
      discount_type: coupon.discount_type || 'percentage',
      discount_value: coupon.discount_value || '',
      min_cart_value: coupon.min_cart_value || 0,
      max_uses: coupon.max_uses || '',
      expires_at: coupon.expires_at ? coupon.expires_at.split('T')[0] : '',
      is_active: coupon.is_active !== false
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.code || !formData.discount_value) {
      toast.error('Please fill required fields');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        discount_value: parseFloat(formData.discount_value),
        min_cart_value: parseFloat(formData.min_cart_value) || 0,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        expires_at: formData.expires_at || null
      };

      if (editingCoupon) {
        await axios.put(`${API}/coupons/${editingCoupon.id}`, payload, getAuthHeaders());
        toast.success('Coupon updated');
      } else {
        await axios.post(`${API}/coupons`, payload, getAuthHeaders());
        toast.success('Coupon created');
      }
      
      setShowModal(false);
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save coupon');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (coupon) => {
    if (!window.confirm(`Delete coupon "${coupon.code}"?`)) return;
    
    try {
      await axios.delete(`${API}/coupons/${coupon.id}`, getAuthHeaders());
      toast.success('Coupon deleted');
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to delete coupon');
    }
  };

  const toggleActive = async (coupon) => {
    try {
      await axios.put(`${API}/coupons/${coupon.id}`, { is_active: !coupon.is_active }, getAuthHeaders());
      toast.success(coupon.is_active ? 'Coupon disabled' : 'Coupon enabled');
      fetchCoupons();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Coupons</h1>
          <p className="text-slate-500 text-sm mt-1">Manage discount codes</p>
        </div>
        <button onClick={openCreateModal} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600">
          <Plus className="w-5 h-5" /> Add Coupon
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
          </div>
        ) : coupons.length === 0 ? (
          <div className="text-center py-12">
            <Ticket className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No coupons yet</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Code</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Discount</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Min Cart</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Usage</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Expires</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {coupons.map((coupon) => (
                <tr key={coupon.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <span className="font-mono font-bold text-slate-900 bg-slate-100 px-2 py-1 rounded">{coupon.code}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1 text-green-600 font-medium">
                      {coupon.discount_type === 'percentage' ? <Percent className="w-4 h-4" /> : <DollarSign className="w-4 h-4" />}
                      {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `₹${coupon.discount_value}`}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">₹{coupon.min_cart_value || 0}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {coupon.used_count || 0} / {coupon.max_uses || '∞'}
                  </td>
                  <td className="px-4 py-3">
                    {coupon.expires_at ? (
                      <span className={isExpired(coupon.expires_at) ? 'text-red-500' : 'text-slate-600'}>
                        {new Date(coupon.expires_at).toLocaleDateString()}
                      </span>
                    ) : <span className="text-slate-400">Never</span>}
                  </td>
                  <td className="px-4 py-3">
                    {!coupon.is_active || isExpired(coupon.expires_at) ? (
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                        {isExpired(coupon.expires_at) ? 'Expired' : 'Disabled'}
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Active</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => toggleActive(coupon)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400">
                        {coupon.is_active ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                      </button>
                      <button onClick={() => openEditModal(coupon)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(coupon)} className="p-1.5 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold">{editingCoupon ? 'Edit Coupon' : 'Create Coupon'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Coupon Code *</label>
                <input type="text" value={formData.code} onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg uppercase" placeholder="SAVE20" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Discount Type</label>
                  <select value={formData.discount_type} onChange={(e) => setFormData({...formData, discount_type: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg">
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Discount Value *</label>
                  <input type="number" value={formData.discount_value} onChange={(e) => setFormData({...formData, discount_value: e.target.value})}
                    min="1" className="w-full px-3 py-2 border border-slate-200 rounded-lg" required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Min Cart Value (₹)</label>
                  <input type="number" value={formData.min_cart_value} onChange={(e) => setFormData({...formData, min_cart_value: e.target.value})}
                    min="0" className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Max Uses</label>
                  <input type="number" value={formData.max_uses} onChange={(e) => setFormData({...formData, max_uses: e.target.value})}
                    min="1" placeholder="Unlimited" className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Expiry Date</label>
                <input type="date" value={formData.expires_at} onChange={(e) => setFormData({...formData, expires_at: e.target.value})}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={formData.is_active} onChange={(e) => setFormData({...formData, is_active: e.target.checked})} className="w-4 h-4" />
                <span className="text-sm">Active</span>
              </label>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border border-slate-200 rounded-lg">Cancel</button>
                <button type="submit" disabled={submitting} className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg disabled:opacity-50">
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingCoupon ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
